import { createContext, useReducer, useEffect } from "react"
import { auth, db } from "../firebase/config"
import { onAuthStateChanged } from "firebase/auth"
import { doc, getDoc } from "firebase/firestore"

export const AuthContext = createContext()

// auth reducer
export const authReducer = (state, action) => {
  switch (action.type) {
    case "LOGIN":
      return {
        ...state,
        user: action.payload,
        firestoreData: action.firestoreData,
        admin: action.isadmin,
        paperManager: action.ispaperManager,
        designer: action.designer,
        semiAdmin: action.semiAdmin,
        accountant: action.isAccountant,
        arsomaUser: action.arsomaUser,
        vivaUser: action.vivaUser,
        warehouse: action.warehouse,
        winder: action.winder,
        tapeUser: action.tapeUser,
        cutter: action.cutter,
        devSpecialist: action.devSpecialist,
        sales: action.sales,
        productionWorker: action.productionWorker,
      }
    case "LOGOUT":
      return {
        ...state,
        user: null,
        firestoreData: null,
        admin: false,
        paperManager: false,
        designer: false,
        semiAdmin: false,
        accountant: false,
        arsomaUser: false,
        vivaUser: false,
        warehouse: false,
        winder: false,
        tapeUser: false,
        cutter: false,
        sales: false,
        devSpecialist: false,
        productionWorker: false,
      }
    case "AUTH_IS_READY":
      return {
        ...state,
        user: action.payload,
        firestoreData: action.firestoreData,
        authIsReady: true,
        admin: action.isadmin,
        paperManager: action.ispaperManager,
        designer: action.designer,
        semiAdmin: action.semiAdmin,
        accountant: action.isAccountant,
        arsomaUser: action.arsomaUser,
        vivaUser: action.vivaUser,
        warehouse: action.warehouse,
        winder: action.winder,
        tapeUser: action.tapeUser,
        cutter: action.cutter,
        sales: action.sales,
        devSpecialist: action.devSpecialist,
        productionWorker: action.productionWorker,
      }
    default:
      return state
  }
}

export const AuthContextProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, {
    user: null,
    firestoreData: null,
    authIsReady: false,
    admin: false,
    paperManager: false,
    designer: false,
    semiAdmin: false,
    accountant: false,
    arsomaUser: false,
    vivaUser: false,
    warehouse: false,
    winder: false,
    tapeUser: false,
    cutter: false,
    sales: false,
    devSpecialist: false,
    productionWorker: false,
  })

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      const handleUserAuth = async (user) => {
        if (!user) {
          dispatch({
            type: "AUTH_IS_READY",
            payload: user,
            firestoreData: null,
            isadmin: false,
            ispaperManager: false,
            designer: false,
            semiAdmin: false,
            accountant: false,
            arsomaUser: false,
            vivaUser: false,
            warehouse: false,
            winder: false,
            tapeUser: false,
            cutter: false,
            sales: false,
            devSpecialist: false,
            productionWorker: false,
          })
          return
        }

        try {
          // Fetch Firestore user data
          const userDoc = await getDoc(doc(db, "users", user.uid))
          const firestoreData = userDoc.exists() ? userDoc.data() : null

          // Get token claims
          const idTokenResult = await auth.currentUser.getIdTokenResult()

          // Check each role individually
          const isAdmin = !!idTokenResult.claims.admin
          const isPaperManager = !!idTokenResult.claims.paperManager
          const isDesigner = !!idTokenResult.claims.designer
          const isSemiAdmin = !!idTokenResult.claims.semiAdmin
          const isAccountant = !!idTokenResult.claims.accountant
          const isArsomaUser = !!idTokenResult.claims.arsomaUser
          const isVivaUser = !!idTokenResult.claims.vivaUser
          const isWarehouse = !!idTokenResult.claims.warehouse
          const isWinder = !!idTokenResult.claims.winder
          const isTapeUser = !!idTokenResult.claims.tapeUser
          const isCutter = !!idTokenResult.claims.cutter
          const isProductionWorker = !!idTokenResult.claims.productionWorker
          // Sales: true if explicitly set OR if user is admin or semiAdmin
          const isSales = !!idTokenResult.claims.sales || isAdmin || isSemiAdmin
          const isDevSpecialist = !!idTokenResult.claims.devSpecialist || isAdmin || isSemiAdmin

          dispatch({
            type: "AUTH_IS_READY",
            payload: user,
            firestoreData,
            isadmin: isAdmin,
            ispaperManager: isPaperManager,
            designer: isDesigner,
            semiAdmin: isSemiAdmin,
            isAccountant: isAccountant,
            arsomaUser: isArsomaUser,
            vivaUser: isVivaUser,
            warehouse: isWarehouse,
            winder: isWinder,
            tapeUser: isTapeUser,
            cutter: isCutter,
            sales: isSales,
            devSpecialist: isDevSpecialist,
            productionWorker: isProductionWorker,
          })
        } catch (error) {
          console.log(error)
        }
      }

      handleUserAuth(user)
    })
    return unsub
  }, [])

  console.log("AuthContext state:", state)

  return <AuthContext.Provider value={{ ...state, dispatch }}>{children}</AuthContext.Provider>
}
